import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Paper,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  TableHead,
  Checkbox,
  Button,
} from '@material-ui/core';

import TrendSettingItem from './TrendSettingItem';
import SaveCancel from '../SaveCancel';
import ColorPicker from '../ColorPicker';
import ListControl from '../ListControl';
import CreateGroupDialog, { CreateGroupDialogResponse } from '../CreateGroupDialog';
import {
  CreateGroupNoComboList,
  CreateInitTrendSettingItem,
  GetDefaultPenColor,
  CreateInitColSetInfo,
  CreateInitGroupSet,
  CreateSpecialDatTrendSettingParameter,
} from './TrendSetting';
import { TableBaseStyle, TrendSettingStyle } from './TrendSettingStyle';

import ItemComboBox, { ComboBoxItemType } from '../../../atoms/ComboBox/ItemComboBox';
import ConfirmDialog, { ConfirmDialogResponse } from '../../../atoms/Dialog/ConfirmDialog';
import MessageDialog, { MessageDialogResponse } from '../../../atoms/Dialog/MessageDialog';
import { SettingsState } from '../../../organisms/SelectSettings';

import {
  GroupListType,
  GetTrendGroupList,
  GetTrendSetting,
  PutTrendGroupSetting,
  TM_TRD_GROUP_SET,
  V_TREND_SETTING_ITEM,
  JudgeInfo,
  PlcInfo,
  ColSetInfo,
  KIND_NO_NAME_SET,
} from '../../../../function/GroupDataModel';
import { useModal } from '../../../../function/useModal';
import { JudgeSettingSelectType, GetJudgeSettingSelectItems } from '../../../../function/JudgeSettingSelectModel';
import { IsConanAirPlc } from '../../../../function/PlcNoModel';
import {
  MeasureHighKind,
  MeasureClcType,
  AlmTypKind,
  GetVibrationSpecialNo,
  GetVibrationSpecialNoUnit,
  GetVibrationSpecialString,
  GetMeasureClcName,
  SpeedKind,
} from '../../../../Constant';
import { SET_PART_DIS } from '../../MeasureSetting/PartialDischargeCollection/PartialDischargeCollection';

type TrendSettingProps = {
  editData: SettingsState;
  groupNo?: number;
  isDialog: boolean;
};

type SaveParameter = {
  group: TM_TRD_GROUP_SET;
  list: V_TREND_SETTING_ITEM[];
  colset: ColSetInfo[];
};

const MaxSettingRow: number = 8;
const MaxGroup: number = 128;

const TrendSettingPage: React.FC<TrendSettingProps> = (props: TrendSettingProps) => {
  const { t, i18n } = useTranslation();
  const tableStyle = TableBaseStyle();
  const pageStyle = TrendSettingStyle();

  const [groupNo, setGroupNo] = useState<number>(0);
  const [editList, setEditList] = useState<TM_TRD_GROUP_SET[]>([]);
  const [groupName, setGroupName] = useState<string>('');
  const [groupCombos, setGroupCombos] = useState<ComboBoxItemType[]>();
  const [measCombos, setMeasCombos] = useState<ComboBoxItemType[]>([]);
  const [deviceCombos, setDeviceCombos] = useState<ComboBoxItemType[]>([]);
  const [itemCombos, setItemCombos] = useState<ComboBoxItemType[]>([]);
  const [dataCombos, setDataCombos] = useState<ComboBoxItemType[]>([]);
  const [alarmCombos, setAlarmCombos] = useState<ComboBoxItemType[]>([]);
  const [mClcTypeCombos, setMClcTypeCombos] = useState<ComboBoxItemType[]>([]);
  const [itemList, setItemList] = useState<V_TREND_SETTING_ITEM[]>([]);
  const [kindNoNameSets, setKindNoNameSets] = useState<KIND_NO_NAME_SET[]>([]);
  const [judgeSetSelect, setJudgeSetSelect] = useState<JudgeSettingSelectType[]>([]);
  const [judgeInfo, setJudgeInfo] = useState<JudgeInfo[]>([]);
  const [deviceUnitInfo, setDeviceUnitInfo] = useState<PlcInfo[]>([]);
  const [colSetInfo, setColSetInfo] = useState<ColSetInfo[]>([]);
  const [cursorColor, setCursorColor] = useState<number>(0);
  const [dispFlg, setDispFlg] = useState<boolean>(false);
  const [minVal, setMinVal] = useState<number>(0.0);
  const [maxVal, setMaxVal] = useState<number>(0.0);
  const [selectIdx, setSelectIdx] = useState<number>(-1);
  const [flgCfmDlg, actCfmDlg] = useModal<ConfirmDialogResponse>();
  const [flgMsgDlg, actMsgDlg] = useModal<MessageDialogResponse>();
  const [dlgmsg, setDlgmsg] = useState<string>('');
  const [flgCrtDlg, actCrtDlg] = useModal<CreateGroupDialogResponse>();
  const [setPartDis, setSetPartDis] = useState<SET_PART_DIS[]>([]);

  const cfmdlg = flgCfmDlg ? (
    <ConfirmDialog
      dialog={actCfmDlg}
      message={dlgmsg}
      action={t('TrendSetting.はい', 'はい')}
      cancel={t('TrendSetting.いいえ', 'いいえ')}
    />
  ) : null;
  const msgdlg = flgMsgDlg ? <MessageDialog dialog={actMsgDlg} message={dlgmsg} /> : null;
  const crtdlg = flgCrtDlg ? (
    <CreateGroupDialog dialog={actCrtDlg} exists={groupCombos?.map<number>((gc) => gc.index)} maxGroup={MaxGroup} />
  ) : null;

  /**
   * 計測種別 言語切替
   */
  const changeLangMeasCombos = () => {
    //振動（速度）
    const idx1 = kindNoNameSets.findIndex((x) => x.NAME_NO == MeasureHighKind.VIBRATION_SPD);
    if (idx1 !== -1) {
      const name = GetVibrationSpecialString(MeasureHighKind.VIBRATION_SPD, t);
      kindNoNameSets[idx1].MES_NAME = name;
      measCombos[idx1].name = name;
    }
    //振動（変位）
    const idx2 = kindNoNameSets.findIndex((x) => x.NAME_NO == MeasureHighKind.VIBRATION_DSP);
    if (idx2 !== -1) {
      const name = GetVibrationSpecialString(MeasureHighKind.VIBRATION_DSP, t);
      kindNoNameSets[idx2].MES_NAME = name;
      measCombos[idx2].name = name;
    }
    //console.debug('changeLangMeasCombos', kindNoNameSets, measCombos, idx1, idx2);

    setKindNoNameSets(kindNoNameSets);
    setMeasCombos(measCombos);
  };

  /**
   * データ種別 言語切替
   */
  const changeLangDataCombos = (newDataCombos: ComboBoxItemType[]) => {
    const data = newDataCombos.find((x) => x.index == MeasureClcType.eChr);
    //console.debug('changeLangDataCombos', data, newDataCombos);
    if (data) {
      data.name = GetMeasureClcName(data.index, data.name, t);
      console.debug(['changeLangDataCombos2', data.name]);
      setDataCombos(
        newDataCombos.map<ComboBoxItemType>((ci) => {
          return { index: ci.index, name: ci.name, tag: ci.tag };
        })
      );
    } else {
      setDataCombos(
        newDataCombos.map<ComboBoxItemType>((ci) => {
          return { index: ci.index, name: ci.name, tag: ci.tag };
        })
      );
    }
  };

  // 言語切替
  useEffect(() => {
    console.debug(['TrendSetting useEffect', i18n, measCombos, dataCombos]);
    changeLangMeasCombos();
    changeLangDataCombos(dataCombos);
  }, [i18n.language]);

  //初期表示
  useEffect(() => {
    GetTrendGroupList().then((result: GroupListType) => {
      setEditList(result.TM_TRD_GROUP_SETs);

      //特殊項目追加
      CreateSpecialDatTrendSettingParameter(result.TrendSettings, t);

      const defkind: KIND_NO_NAME_SET = { SPEED: -1, NAME_NO: -1, MES_NAME: '' };
      setKindNoNameSets([defkind].concat(result.TrendSettings.KIND_NO_NAME_SETs));

      const defPara: ComboBoxItemType = { index: 0, name: '' };
      //計測種別
      //console.debug('setMeasCombos', result.TrendSettings.MeasKinds);
      setMeasCombos(
        [defPara].concat(
          result.TrendSettings.MeasKinds.map<ComboBoxItemType>((ci) => {
            return { index: ci.Key, name: ci.Disp, tag: ci.Tag };
          })
        )
      );
      setDeviceCombos(
        [defPara].concat(
          result.TrendSettings.DeviceLists.map<ComboBoxItemType>((ci) => {
            return { index: ci.Key, name: ci.Disp };
          })
        )
      );
      setItemCombos(
        [defPara].concat(
          result.TrendSettings.ItemLists.map<ComboBoxItemType>((ci) => {
            return { index: ci.Key, name: ci.Disp };
          })
        )
      );
      //演算種別
      setMClcTypeCombos(
        [defPara].concat(
          result.TrendSettings.MClcTypes.map<ComboBoxItemType>((ci) => {
            return { index: ci.Key, name: ci.Disp, tag: ci.Tag };
          })
        )
      );
      //データ種別
      //console.debug('setDataCombos', result.TrendSettings.DataKinds);
      const newDataCombos = result.TrendSettings.DataKinds.map<ComboBoxItemType>((ci) => {
        return { index: ci.Key, name: ci.Disp, tag: ci.Tag };
      });
      //console.debug('setDataCombos2', newDataCombos);
      changeLangDataCombos(newDataCombos);

      //異常種別
      setAlarmCombos(
        [defPara].concat(
          result.TrendSettings.AlarmKinds.map<ComboBoxItemType>((ci) => {
            return { index: ci.Key, name: ci.Disp, tag: ci.Tag };
          })
        )
      );
      setJudgeInfo(result.TrendSettings.AlarmLimits);
      setDeviceUnitInfo(result.TrendSettings.DeviceUnitInfos);
      setColSetInfo(result.TrendSettings.ColSetInfos);
      setJudgeSetSelect(result.TrendSettings.JudgeSetSelect);
      setSetPartDis(result.TrendSettings.SetPartDisList);

      props.editData.SaveFunction = saveCurrentSetting;
      props.editData.IsUpdate = false;

      if (props.groupNo) {
        setGroupNo(props.groupNo);
      }

      console.debug(['result', result]);
    });
  }, [props.editData.CancelCondition]);

  useEffect(() => {
    const groups = CreateGroupNoComboList(editList);
    setGroupCombos(groups);

    if (!editList.find((el) => el.GROUP_NO == groupNo)) {
      if (editList.length > 0) {
        const grp = editList[0];
        setGroupNo(grp.GROUP_NO);
      }
    }
  }, [editList]);

  //グループNo変更表示処理
  useEffect(() => {
    console.log('-- groupChange --', groupNo);
    setSelectIdx(-1);
    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      console.log('groupName', grp.GROUP_NAME);
      setGroupName(grp.GROUP_NAME ?? '');
      setCursorColor(grp.DSP_COLOR);
      setDispFlg(grp.DSP_FLG !== 0);
    } else {
      console.log('not found group');
    }

    GetTrendSetting(groupNo).then((result: V_TREND_SETTING_ITEM[]) => {
      var setItems: V_TREND_SETTING_ITEM[] = [];
      //KINDを確認して、特殊な信号だった場合KIND_NOを入れ替える
      //MCLC_TYP_IDがNULLなら、デフォルトで０を設定
      result.forEach((r) => {
        //コナンエアの場合SET_NO＝KIND_NOとする
        if (IsConanAirPlc(String(r.PLC_NO))) {
          r.KIND_NO = r.SET_NO;
        }
        var spNo = GetVibrationSpecialNo(r.KIND);
        if (spNo !== '0') {
          r.KIND_NO = Number(spNo);
        }
        if (r.MCLC_TYP_ID === null) {
          r.MCLC_TYP_ID = 0;
        }
      });
      for (let step = 0; step < MaxSettingRow; step++) {
        const penNo = step + 1;
        const index = result.findIndex((r) => r.PEN_NO == penNo);
        if (index === -1) {
          let insRow = CreateInitTrendSettingItem(groupNo);
          insRow.PEN_NO = penNo;
          insRow.DSP_COLOR = GetDefaultPenColor(penNo);
          setItems.push(insRow);
        } else {
          setItems.push(result[index]);
        }
      }

      console.debug(['result,setItemList', result, setItems]);
      setItemList(setItems);
      if (result.length > 0) {
        setMinVal(result[0].GRP_LOW);
        setMaxVal(result[0].GRP_HIGH);
      }
    });
  }, [groupNo]);

  /**
   * グルプ名変更event
   * @param name
   */
  const groupNameChange = (name: string) => {
    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      setGroupName(name);
      grp.GROUP_NAME = name;

      props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };

  const saveCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.group && sp.list) {
        const errmsg = validateSetting(sp.group, sp.list, sp.colset);
        if (errmsg === '') {
          console.debug([
            'saveCurrentSetting - validateSetting OK sp.group, sp.list, sp.colset',
            sp.group,
            sp.list,
            sp.colset,
          ]);
          PutTrendGroupSetting(sp.group, sp.list)
            .then((count: number) => {
              console.log(['saveCurrentSetting end', count]);
              resolve('done');
            })
            .catch((reason) => reject(reason));
        } else {
          setDlgmsg(errmsg);
          actMsgDlg.open().apply((_) => setDlgmsg(''));
        }
      } else {
        reject('data nothing');
      }
    });
  };
  const deleteCurrentSetting = (param: object): Promise<string> => {
    return new Promise((resolve, reject) => {
      const sp = param as SaveParameter;
      if (sp && sp.group && sp.list) {
        PutTrendGroupSetting(sp.group, sp.list)
          .then((count: number) => {
            console.log(['deleteCurrentSetting end', count]);
            resolve('done');
          })
          .catch((reason) => reject(reason));
      } else {
        reject('data nothing');
      }
    });
  };
  const itemUpdate = (item: V_TREND_SETTING_ITEM) => {
    const idx = itemList.findIndex((itm) => itm.PEN_NO === item.PEN_NO);
    if (idx >= 0) {
      const temp = itemList.concat(); // 画面更新のためArrayの再生成

      let findCol;
      if (item.SPEED === SpeedKind.SPEED_CONAN) {
        findCol = colSetInfo.find(
          (csi) => csi.DEV_INDEX === item.DEV_INDEX && csi.SPEED === item.SPEED && csi.ITM_NAME_NO === item.ITM_NAME_NO
        );

        if (findCol) {
          item.PLC_NO = findCol.PLC_NO;
          item.AI_CARD = findCol.AI_CARD;
          item.AI_CH = findCol.AI_CH;
          item.UNIT = findCol.UNIT;
        }
      } else {
        findCol = colSetInfo.find(
          (csi) =>
            csi.KIND_NO === item.KIND_NO &&
            csi.DEV_INDEX === item.DEV_INDEX &&
            csi.ITM_NAME_NO === item.ITM_NAME_NO &&
            csi.SPEED === item.SPEED
        );
        if (findCol) {
          item.PLC_NO = findCol.PLC_NO;
          item.AI_CARD = findCol.AI_CARD;
          item.AI_CH = findCol.AI_CH;
          item.SET_NO = findCol.SET_NO;
          item.UNIT = findCol.UNIT;
          item.KIND_NO = findCol.KIND_NO;
        }
      }
      console.debug([
        'item,colSetInfo,findCol',
        item,
        colSetInfo,
        findCol,
        colSetInfo.filter((r) => r.SPEED == 2 && r.KIND_NO == 2),
      ]);

      temp[idx] = item;
      setItemList(temp);

      const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
      props.editData.Target = { group: grp, list: temp, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };

  const updateCursorColor = (color: number) => {
    setCursorColor(color);

    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.DSP_COLOR = color;

      props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };

  //指定したアイテムが行データとして有効か確認
  const checkValidityItem = (item: V_TREND_SETTING_ITEM): boolean => {
    if (item.SPEED !== 0 && item.KIND_NO !== 0) {
      return true;
    }
    if (item.DEV_INDEX !== 0) {
      return true;
    }
    if (item.ITM_NAME_NO !== 0) {
      return true;
    }
    return false;
  };

  const conditionRow = (source: V_TREND_SETTING_ITEM[]): V_TREND_SETTING_ITEM[] => {
    const temp = source.concat();
    if (temp.length < MaxSettingRow) {
      temp.push(CreateInitTrendSettingItem(groupNo));
    } else if (temp.length > MaxSettingRow) {
      temp.pop();
    }
    temp.forEach((item, idx) => (item.PEN_NO = idx + 1));
    return temp;
  };
  const insertRow = () => {
    if (selectIdx == -1) {
      return;
    }
    const last = itemList.slice(-1)[0];
    if (checkValidityItem(last)) {
      //インサートできない
      return;
    }
    //指定した行へ空データインサート
    itemList.splice(selectIdx, 0, CreateInitTrendSettingItem(groupNo));
    //末尾を削除
    itemList.pop();
    //ペンNo振りなおし
    itemList.forEach((item, idx) => (item.PEN_NO = idx + 1));
    for (let i = 0; i < itemList.length; i++) {
      if (itemList[i].DEV_INDEX === 0) {
        itemList[i].DSP_COLOR = GetDefaultPenColor(i + 1);
      }
    }
    console.debug(['setItemList', itemList]);
    setItemList(itemList.concat());

    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };
  const deleteRow = () => {
    if (selectIdx == -1) {
      return;
    }
    //指定行を削除
    itemList.splice(selectIdx, 1);
    //末尾に行追加
    itemList.push(CreateInitTrendSettingItem(groupNo));
    //ペンNo振りなおし
    itemList.forEach((item, idx) => (item.PEN_NO = idx + 1));
    for (let i = 0; i < itemList.length; i++) {
      if (itemList[i].DEV_INDEX === 0) {
        itemList[i].DSP_COLOR = GetDefaultPenColor(i + 1);
      }
    }

    console.debug(['setItemList', itemList]);
    setItemList(itemList.concat());

    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };
  const upRow = () => {
    if (selectIdx == -1) {
      setSelectIdx(0);
      return;
    }
    if (selectIdx > 0) {
      const trg = itemList[selectIdx];
      itemList[selectIdx] = itemList[selectIdx - 1];
      itemList[selectIdx - 1] = trg;
      if (itemList[selectIdx].DEV_INDEX === 0) {
        itemList[selectIdx].DSP_COLOR = GetDefaultPenColor(selectIdx + 1);
      }
      setItemList(conditionRow(itemList));
      setSelectIdx(selectIdx - 1);

      const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
      if (grp) {
        props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
        props.editData.IsUpdate = true;
      }
    }
  };
  const downRow = () => {
    if (selectIdx == -1) {
      setSelectIdx(0);
      return;
    }
    if (selectIdx < MaxSettingRow - 1) {
      const trg = itemList[selectIdx];
      itemList[selectIdx] = itemList[selectIdx + 1];
      itemList[selectIdx + 1] = trg;
      if (itemList[selectIdx].DEV_INDEX === 0) {
        itemList[selectIdx].DSP_COLOR = GetDefaultPenColor(selectIdx + 1);
      }
      setItemList(conditionRow(itemList));
      setSelectIdx(selectIdx + 1);

      const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
      if (grp) {
        props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
        props.editData.IsUpdate = true;
      }
    }
  };
  const updateDisp = (disp: boolean) => {
    setDispFlg(disp);
    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    if (grp) {
      grp.DSP_FLG = disp ? 1 : 0;

      props.editData.Target = { group: grp, list: itemList, colset: colSetInfo };
      props.editData.IsUpdate = true;
    }
  };
  useEffect(() => {
    itemList.forEach((item) => (item.GRP_LOW = minVal));
  }, [minVal]);
  useEffect(() => {
    itemList.forEach((item) => (item.GRP_HIGH = maxVal));
  }, [maxVal]);

  const allDisp = () => {
    itemList.forEach((itm) => {
      itm.DSP_FLG = 1;
    });
    const temp = itemList.concat(); // 画面更新のためArrayの再生成
    setItemList(temp);

    const grp = editList.find((ed) => ed.GROUP_NO === groupNo);
    props.editData.Target = { group: grp, list: temp, colset: colSetInfo };
    props.editData.IsUpdate = true;
  };

  /**
   * 保存前の検証設定 
   * @param group 
   * @param items 
   * @param colsets 
   * @returns 
   */
  const validateSetting = (group: TM_TRD_GROUP_SET, items: V_TREND_SETTING_ITEM[], colsets: ColSetInfo[]): string => {
    if (group.GROUP_NAME == '') {
      return t('TrendSetting.グループ名必須', 'グループ名は必須です');
    }
    /* 全行の空チェック */
    if (items.filter((r) => r.DEV_INDEX !== 0).length === 0) {
      return t('TrendSetting.リストが空のためグループが削除されます', 'リストが空のためグループが削除されます');
    }

    for (var idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      const itemNoInfo: string = 'no:' + String(idx + 1) + ' ';
      console.debug(['colsets,item, index', colsets, item, idx]);
      if (checkValidityItem(item)) {
        let trg;
        if (item.SPEED === SpeedKind.SPEED_CONAN) {
          //コナンエアの場合
          trg = colsets.find((colset) => {
            return (
              colset.DEV_INDEX === item.DEV_INDEX &&
              colset.SPEED === item.SPEED &&
              colset.ITM_NAME_NO === item.ITM_NAME_NO
            );
          });
        }
        else if (item.SPEED === SpeedKind.SPEED_PARTIALDISCHARGE) {
          //部分放電の場合
          trg = setPartDis.find((spd) => {
            console.debug(["部分放電の場合", spd, item])
            return (
              spd.DEV_INDEX === item.DEV_INDEX &&
              spd.ITM_NAME_NO === item.ITM_NAME_NO
            )
          });
        }
        else {
          trg = colsets.find((colset) => {
            return (
              colset.KIND_NO === item.KIND_NO &&
              colset.DEV_INDEX === item.DEV_INDEX &&
              colset.ITM_NAME_NO === item.ITM_NAME_NO &&
              colset.SPEED === item.SPEED
            );
          });
        }

        if (trg) {
          if (item.KIND_NO !== 0 && item.DEV_INDEX !== 0 && item.ITM_NAME_NO !== 0) {
            if (item.CLC === 0) {
              return (
                itemNoInfo + t('TrendSetting.登録データが不正です。(データ種別)', '登録データが不正です。(データ種別)')
              );
            }
            if (item.MCLC_TYP_ID === 0) {
              return (
                itemNoInfo + t('TrendSetting.登録データが不正です。(演算種別)', '登録データが不正です。(演算種別)')
              );
            }
            if (item.TYP_NO === 0) {
              return (
                itemNoInfo + t('TrendSetting.登録データが不正です。(異常内容)', '登録データが不正です。(異常内容)')
              );
            }
          }
        } else {
          return (
            itemNoInfo +
            t(
              'TrendSetting.指定された項目が見つかりません。\n(機器番号・項目名称からPLCNo.、AIカードNo.、CHNo.を検索)',
              '指定された項目が見つかりません。\n(機器番号・項目名称からPLCNo.、AIカードNo.、CHNo.を検索)'
            )
          );
        }
      }
    }
    return '';
  };

  return (
    <div>
      <TableContainer>
        <div className={pageStyle.itemRow}>
          <div className={pageStyle.itemSelLabel}>{t('TrendSetting.グループNo', 'グループNo')}</div>
          <div className={pageStyle.itemSelData}>
            <ItemComboBox
              itemList={groupCombos}
              label=""
              value={groupNo}
              onChangeEvent={(e) => {
                if (typeof e.target.value === 'number') {
                  setGroupNo(e.target.value);
                } else if (typeof e.target.value === 'string') {
                  setGroupNo(parseInt(e.target.value));
                }
              }}
            />
          </div>
          <div className={pageStyle.itemSelLabel}>{t('TrendSetting.グループ名称', 'グループ名称')}</div>
          <div className={pageStyle.itemSelData}>
            <TextField
              value={groupName}
              inputProps={{ maxLength: 32 }}
              onChange={(e) => {
                groupNameChange(e.target.value);
              }}
            />
          </div>
        </div>
        {/* <div className={pageStyle.itemRow}>
          <div className={pageStyle.itemSelLabel}>{t('TrendSetting.表示', '表示')}</div>
          <div className={pageStyle.itemSelData}>
            <Checkbox
              checked={dispFlg}
              onChange={(e) => {
                updateDisp(e.target.checked);
              }}
            />
          </div>
          <div className={classes.headSelLabel}>{t('TrendSetting.グラフ最小値', 'グラフ最小値')}</div>
          <div className={classes.headSelData}>
            <NumericEntry
              value={minVal}
              fixed={2}
              className={classes.entry}
              onChange={(val) => {
                setMinVal(val);
              }}
            />
          </div>
          <div className={classes.entryLabel}>{t('TrendSetting.グラフ最大値', 'グラフ最大値')}</div>
          <div className={classes.headSelData}>
            <NumericEntry
              value={maxVal}
              fixed={2}
              className={classes.entry}
              onChange={(val) => {
                setMaxVal(val);
              }}
            />
          </div>
          <div className={pageStyle.entryLabel}>{t('TrendSetting.カーソル', 'カーソル')}</div>
          <div className={pageStyle.itemSelData}>
            <ColorPicker color={cursorColor} setColor={updateCursorColor} />
          </div>
        </div> */}

        <div className={pageStyle.settingContainer}>
          <Table className={pageStyle.gridStyle} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={tableStyle.headerCell} colSpan={2}>
                  {t('TrendSetting.表示', '表示')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.計測種別', '計測種別')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.機器番号_機器名称', '機器番号・機器名称')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.項目名称', ' 項目名称')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.データ種別', 'データ種別')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.演算種別', '演算種別')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.異常種別', '異常種別')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} colSpan={2}>
                  {t('TrendSetting.警報閾値', '警報閾値')}
                </TableCell>
                <TableCell className={tableStyle.headerCell} rowSpan={2}>
                  {t('TrendSetting.単位', '単位')}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={tableStyle.headerCell_s}>{t('TrendSetting.有', '有')}</TableCell>
                <TableCell className={tableStyle.headerCell_s}>{t('TrendSetting.線', '線')}</TableCell>
                <TableCell className={tableStyle.headerCell}>{t('TrendSetting.軽故障', '軽故障')}</TableCell>
                <TableCell className={tableStyle.headerCell}>{t('TrendSetting.重故障', '重故障')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemList.map((item, idx) => {
                return (
                  <TrendSettingItem
                    key={idx}
                    rowno={idx}
                    selectIdx={selectIdx}
                    setSelectIdx={setSelectIdx}
                    item={item}
                    updateItem={itemUpdate}
                    measKinds={measCombos}
                    deviceLists={deviceCombos}
                    itemLists={itemCombos}
                    dataKinds={dataCombos}
                    alarmKinds={alarmCombos}
                    alarmLimits={judgeInfo}
                    colSetInfos={colSetInfo}
                    kindNoNameSets={kindNoNameSets}
                    judgeSetSelect={judgeSetSelect}
                    mClcTypes={mClcTypeCombos}
                    setPartDis={setPartDis}
                  />
                );
              })}
            </TableBody>
          </Table>
          <ListControl
            insertAction={() => insertRow()}
            deleteAction={() => deleteRow()}
            upAction={() => upRow()}
            downAction={() => downRow()}
          />
        </div>
        <div className={pageStyle.container}>
          <Button
            className={pageStyle.button}
            variant="contained"
            color="primary"
            onClick={(e) => {
              allDisp();
            }}
          >
            {t('TrendSetting.すべて表示', 'すべて表示')}
          </Button>

          <SaveCancel
            saveAction={() => {
              setDlgmsg(t('TrendSetting.保存しますか?', '保存しますか?'));
              actCfmDlg
                .open()
                .apply((_) => {
                  const grp = editList.find((ed) => ed.GROUP_NO == groupNo);
                  if (grp) {
                    saveCurrentSetting({ group: grp, list: itemList, colset: colSetInfo }).then((_) => {
                      props.editData.IsSaveClose = true;
                      props.editData.IsUpdate = false;
                      if (props.isDialog) {
                        props.editData.CloseSetting();
                      }
                    });
                  }
                })
                .cancel(() => { });
            }}
            cancelAction={() => {
              if (props.editData.IsUpdate) {
                setDlgmsg(
                  t(
                    'TrendSetting.保存していないデータを破棄します。\nよろしいですか?',
                    '保存していないデータを破棄します。\nよろしいですか?'
                  )
                );
                actCfmDlg
                  .open()
                  .apply((_) => {
                    if (props.isDialog) {
                      props.editData.IsUpdate = false;
                      props.editData.CloseSetting();
                    } else {
                      setEditList([]);
                      setGroupNo(0);
                      props.editData.UpdateCancel();
                    }
                  })
                  .cancel(() => { });
              } else {
                if (props.isDialog) {
                  props.editData.CloseSetting();
                }
              }
            }}
            createAction={() => {
              actCrtDlg.open().apply((info) => {
                const grp = editList.find((ed) => ed.GROUP_NO == info.no);
                if (!grp) {
                  const newGrp = CreateInitGroupSet(info.no);
                  const newList = editList.concat(newGrp).sort((g1, g2) => g1.GROUP_NO - g2.GROUP_NO);

                  setEditList(newList);
                }
                setGroupNo(info.no);
              });
            }}
            deleteAction={() => {
              setDlgmsg(t('TrendSetting.削除しますか?', '削除しますか?'));
              actCfmDlg.open().apply((_) => {
                const grp = editList.find((ed) => ed.GROUP_NO == groupNo);
                if (grp) {
                  grp.GROUP_NAME = '';
                  deleteCurrentSetting({ group: grp, list: [], colset: colSetInfo }).then((_) => {
                    props.editData.IsSaveClose = true;
                    props.editData.IsUpdate = false;
                    const newEditList = editList.filter((ed) => ed.GROUP_NO != groupNo);
                    setEditList(newEditList);
                  });
                }
              });
            }}
          />
        </div>
      </TableContainer>
      {cfmdlg}
      {msgdlg}
      {crtdlg}
    </div>
  );
};

TrendSettingPage.defaultProps = { isDialog: false };

export default TrendSettingPage;
